import { useMemo } from "react";
import { useSortBy, useTable } from "react-table";
import { COLORS, TEXT_COLORS } from "../../../constants/design/colors";
import {
  IIsolatedHealthPosition,
  IsolatedRiskStatus,
} from "../../../interfaces/aevo";
import {
  compareDecimalStrings,
  formatDecimalString,
} from "../../../utils/decimal";
import { DefaultCellForColumn } from "../../Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../../Table/DefaultHeaderForColumn";
import { ITableColumn } from "../../Table/TableColumn";
import {
  Align,
  LeftTitleWrapper,
  TableHeaderCell,
  TableLoader,
  TableWrapper,
  TitleWrapper,
} from "../../Table/style";

interface IIsolatedHealthTableProps {
  positions: IIsolatedHealthPosition[];
  isLoading?: boolean;
}

interface IStatusStyle {
  text: string;
  background: string;
}

const STATUS_STYLES: Record<IsolatedRiskStatus, IStatusStyle> = {
  liquidating: {
    text: COLORS.orange.one,
    background: COLORS.orange.four,
  },
  breached: {
    text: COLORS.red.one,
    background: COLORS.red.four,
  },
  warning: {
    text: "#FACC15",
    background: "rgba(250, 204, 21, 0.16)",
  },
  healthy: {
    text: COLORS.green.one,
    background: COLORS.green.four,
  },
};

const STATUS_RANK: Record<IsolatedRiskStatus, number> = {
  liquidating: 0,
  breached: 1,
  warning: 2,
  healthy: 3,
};

const STATUS_LABELS: Record<IsolatedRiskStatus, string> = {
  liquidating: "LIQUIDATING",
  breached: "BREACHED",
  warning: "WARNING",
  healthy: "HEALTHY",
};

const decimalSortType = (rowA: any, rowB: any, columnId: string) =>
  compareDecimalStrings(rowA.values[columnId], rowB.values[columnId]);

const getStatusRank = (status: unknown): number => {
  if (
    typeof status === "string" &&
    Object.prototype.hasOwnProperty.call(STATUS_RANK, status)
  ) {
    return STATUS_RANK[status as IsolatedRiskStatus];
  }

  return STATUS_RANK.healthy;
};

const formatUsd = (
  value: string,
  maxFractionDigits: number = 2,
  fallback: string = "-"
) => {
  if (!value || value === "-") {
    return fallback;
  }

  const formatted = formatDecimalString(value, {
    maxFractionDigits,
    minFractionDigits: 0,
  });

  return formatted === "-" ? fallback : `$${formatted}`;
};

const formatNumber = (
  value: string,
  maxFractionDigits: number = 4,
  fallback: string = "-"
) =>
  formatDecimalString(value, {
    maxFractionDigits,
    minFractionDigits: 0,
    fallback,
  });

const getPositionSide = (position: string): "LONG" | "SHORT" | "-" => {
  const direction = compareDecimalStrings(position, "0");
  if (direction > 0) {
    return "LONG";
  }
  if (direction < 0) {
    return "SHORT";
  }
  return "-";
};

const getPositionSideColor = (side: "LONG" | "SHORT" | "-") => {
  if (side === "LONG") {
    return COLORS.green.one;
  }
  if (side === "SHORT") {
    return COLORS.red.one;
  }
  return TEXT_COLORS.two;
};

const getPnLColor = (value: string) => {
  const cmp = compareDecimalStrings(value, "0");
  if (cmp > 0) {
    return COLORS.green.one;
  }
  if (cmp < 0) {
    return COLORS.red.one;
  }
  return TEXT_COLORS.one;
};

export function IsolatedHealthTable({
  positions,
  isLoading,
}: IIsolatedHealthTableProps) {
  const columns: ITableColumn<IIsolatedHealthPosition>[] = useMemo(
    () => [
      {
        title: "Status",
        accessor: "risk_status",
        align: "left",
        sortType: (rowA: any, rowB: any, columnId: string) =>
          getStatusRank(rowA.values[columnId]) -
          getStatusRank(rowB.values[columnId]),
        Cell: ({ value }) => {
          const status = value as IsolatedRiskStatus;
          const style = STATUS_STYLES[status];

          return (
            <span
              style={{
                color: style.text,
                backgroundColor: style.background,
                borderRadius: 6,
                padding: "4px 8px",
                fontSize: 11,
                letterSpacing: "0.03em",
              }}
            >
              {STATUS_LABELS[status]}
            </span>
          );
        },
      },
      {
        title: "Instrument",
        accessor: "instrument_name",
        align: "left",
      },
      {
        title: "Asset",
        accessor: "asset",
        align: "left",
      },
      {
        title: "Side",
        id: "position_side",
        accessor: "position",
        align: "left",
        Cell: ({ value }) => {
          const side = getPositionSide(value);
          const color = getPositionSideColor(side);

          return <span style={{ color }}>{side}</span>;
        },
      },
      {
        title: "Position",
        accessor: "position",
        align: "right",
        sortType: decimalSortType,
        valueExtractor: (value) => formatNumber(value),
      },
      {
        title: "Entry Price",
        accessor: "entry_price",
        align: "right",
        sortType: decimalSortType,
        valueExtractor: (value) => formatUsd(value, 6),
      },
      {
        title: "Mark Price",
        accessor: "mark_price",
        align: "right",
        sortType: decimalSortType,
        valueExtractor: (value) => formatUsd(value, 6),
      },
      {
        title: "Allocated Margin",
        accessor: "allocated_isolated_margin",
        align: "right",
        sortType: decimalSortType,
        valueExtractor: (value) => formatUsd(value, 4),
      },
      {
        title: "Base Margin",
        accessor: "base_isolated_margin",
        align: "right",
        sortType: decimalSortType,
        valueExtractor: (value) => formatUsd(value, 4),
      },
      {
        title: "UPNL",
        accessor: "upnl",
        align: "right",
        sortType: decimalSortType,
        Cell: ({ value }) => {
          const color = getPnLColor(value);

          return (
            <Align align="right" style={{ color }}>
              {formatUsd(value, 4)}
            </Align>
          );
        },
      },
      {
        title: "Maintenance Margin",
        accessor: "maintenance_margin",
        align: "right",
        sortType: decimalSortType,
        valueExtractor: (value) => formatUsd(value, 4),
      },
      {
        title: "Liquidation Price",
        accessor: "liquidation_price",
        align: "right",
        sortType: decimalSortType,
        valueExtractor: (value) =>
          value && value !== "-" ? formatUsd(value, 6) : "-",
      },
      {
        title: "In Liquidation",
        accessor: "in_liquidation",
        align: "left",
        Cell: ({ value }) => (
          <span style={{ color: value ? COLORS.orange.one : TEXT_COLORS.two }}>
            {value ? "Yes" : "No"}
          </span>
        ),
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: positions,
        defaultColumn: {
          Header: DefaultHeaderForColumn,
          Cell: DefaultCellForColumn,
        } as any,
        autoResetSortBy: false,
      },
      useSortBy
    );

  return (
    <div>
      <TitleWrapper>
        <LeftTitleWrapper>
          <h3>Isolated Positions {positions.length ? `(${positions.length})` : "(None)"}</h3>
        </LeftTitleWrapper>
      </TitleWrapper>
      <TableWrapper>
        <TableLoader isLoading={isLoading} />
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              // eslint-disable-next-line react/jsx-key
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column: any) => (
                  // eslint-disable-next-line react/jsx-key
                  <TableHeaderCell
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    style={{ width: column.width }}
                  >
                    {column.render("Header")}
                  </TableHeaderCell>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              const { ...rowProps } = row.getRowProps();

              return (
                // eslint-disable-next-line react/jsx-key
                <tr {...rowProps}>
                  {row.cells.map((cell) => (
                    // eslint-disable-next-line react/jsx-key
                    <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableWrapper>
    </div>
  );
}
